import React, { useState, useEffect, useRef } from 'react'; // Import useRef
import { marked } from 'marked'; // Import the marked library

// Main App component for the Property Listing Report Generator
function App() {
    // State variables for all input fields
    const [propertyAddress, setPropertyAddress] = useState('');
    const [propertyImageUrl, setPropertyImageUrl] = useState('');
    const [listingAge, setListingAge] = useState('');
    const [propertyWebsiteLink, setPropertyWebsiteLink] = useState('');

    // YouTube metrics
    const [youtubeViews, setYoutubeViews] = useState('');
    const [youtubeWatchHours, setYoutubeWatchHours] = useState('');
    const [youtubeWatchedEntire, setYoutubeWatchedEntire] = useState('');
    const [youtubeAvgWatched, setYoutubeAvgWatched] = useState('');
    const [youtubeLink, setYoutubeLink] = useState('');

    // Zillow metrics
    const [zillowViews, setZillowViews] = useState('');
    const [zillowSaves, setZillowSaves] = useState('');
    const [zillowLink, setZillowLink] = useState('');

    // MLS metrics
    const [mlsMatchedBuyers, setMlsMatchedBuyers] = useState('');
    const [mlsSaves, setMlsSaves] = useState('');

    // RealScout metrics
    const [realscoutMatchedBuyers, setRealscoutMatchedBuyers] = useState('');
    const [realscoutSaves, setRealscoutSaves] = useState('');

    // Showings and Feedback
    const [pastShowings, setPastShowings] = useState('');
    const [futureShowings, setFutureShowings] = useState('');
    const [openHouses, setOpenHouses] = useState('');
    const [feedbackSummary, setFeedbackSummary] = useState('');

    // Dynamic "Other Marketing Data" fields
    const [otherMarketingData, setOtherMarketingData] = useState([]);
    const [marketingAssetsLink, setMarketingAssetsLink] = useState('');

    // Paid Campaign metrics (Multiple campaigns support)
    const [paidCampaigns, setPaidCampaigns] = useState([]);

    // Email Marketing metrics
    const [emailDatabaseSize, setEmailDatabaseSize] = useState('');
    const [emailOpenRate, setEmailOpenRate] = useState('');
    const [emailCtr, setEmailCtr] = useState('');

    // Social Media Specifics
    const [socialShares, setSocialShares] = useState('');
    const [socialComments, setSocialComments] = useState('');

    // Market Positioning & Competitive Context
    const [marketAvgDom, setMarketAvgDom] = useState('');
    const [activeCompetitors, setActiveCompetitors] = useState('');
    const [recentSales, setRecentSales] = useState('');
    const [agentRecommendation, setAgentRecommendation] = useState('');
    const [upcomingActivity, setUpcomingActivity] = useState('');

    // Trend Indicators
    const [youtubeViewsTrend, setYoutubeViewsTrend] = useState('none');
    const [zillowViewsTrend, setZillowViewsTrend] = useState('none');
    const [zillowSavesTrend, setZillowSavesTrend] = useState('none');
    const [mlsSavesTrend, setMlsSavesTrend] = useState('none');

    // Branding & Visual Customization
    const [primaryColor, setPrimaryColor] = useState('#334155');
    const [accentColor, setAccentColor] = useState('#f8fafc');
    const [brokerageLogoUrl, setBrokerageLogoUrl] = useState('');
    const [reportTitle, setReportTitle] = useState('Property Listing Report');

    // Local Market Performance & Context
    const [interestRateContext, setInterestRateContext] = useState('');
    const [localInventory, setLocalInventory] = useState('');
    const [buyerDemandLevel, setBuyerDemandLevel] = useState('none');
    const [marketCondition, setMarketCondition] = useState('none');
    const [marketHeadwinds, setMarketHeadwinds] = useState('');
    const [showMarketPulse, setShowMarketPulse] = useState(true);


    // Agent Information
    const [agentName, setAgentName] = useState('');
    const [agentPhotoUrl, setAgentPhotoUrl] = useState('');
    const [licenseNumber, setLicenseNumber] = useState('');
    const [agentPhone, setAgentPhone] = useState('');
    const [agentEmail, setAgentEmail] = useState('');
    const [brokerageName, setBrokerageName] = useState('');
    const [brokeragePhone, setBrokeragePhone] = useState('');
    const [brokerageLicense, setBrokerageLicense] = useState('');

    // State for the generated HTML report
    const [reportHtml, setReportHtml] = useState('');

    // Ref for the iframe element to access its content
    const iframeRef = useRef(null);

    // Load data from URL or local storage on component mount
    useEffect(() => {
        let agentInfoFromLocalStorage = {};
        try {
            const savedAgentInfo = localStorage.getItem('agentInfo');
            if (savedAgentInfo) {
                agentInfoFromLocalStorage = JSON.parse(savedAgentInfo);
                setAgentName(agentInfoFromLocalStorage.agentName || '');
                setAgentPhotoUrl(agentInfoFromLocalStorage.agentPhotoUrl || '');
                setLicenseNumber(agentInfoFromLocalStorage.licenseNumber || '');
                setAgentPhone(agentInfoFromLocalStorage.agentPhone || '');
                setAgentEmail(agentInfoFromLocalStorage.agentEmail || '');
                setBrokerageName(agentInfoFromLocalStorage.brokerageName || '');
                setBrokeragePhone(agentInfoFromLocalStorage.brokeragePhone || '');
                setBrokerageLicense(agentInfoFromLocalStorage.brokerageLicense || '');
                // Branding persistence
                setBrokerageLogoUrl(agentInfoFromLocalStorage.brokerageLogoUrl || '');
                setPrimaryColor(agentInfoFromLocalStorage.primaryColor || '#334155');
                setAccentColor(agentInfoFromLocalStorage.accentColor || '#f8fafc');
                setReportTitle(agentInfoFromLocalStorage.reportTitle || 'Property Listing Report');
            }

        } catch (error) {
            console.error("Failed to load agent info from local storage:", error);
        }

        const urlParams = new URLSearchParams(window.location.search);
        const data = urlParams.get('data');

        const loadData = async () => {
            const urlParams = new URLSearchParams(window.location.search);
            const data = urlParams.get('data');

            if (data) {
                try {
                    let stateData;
                    // Try to parse as v1 (Base64 JSON)
                    try {
                        const decodedData = JSON.parse(atob(data));
                        // If it's valid JSON but doesn't have the compression flag, it's v1
                        if (decodedData.v !== 2) {
                            stateData = decodedData;
                        } else {
                            // This case shouldn't happen with the new merged v2, 
                            // but we check just in case legacy v2 (JSON-only) exists
                            stateData = decodedData;
                        }
                    } catch (e) {
                        // If JSON parse fails, it's likely a v2 GZIP compressed link
                        stateData = await decompressData(data);

                        // Apply reverse key mapping for v2
                        const reverseMap = Object.fromEntries(Object.entries(KEY_MAP).map(([k, v]) => [v, k]));
                        const mappedData = {};
                        Object.keys(stateData).forEach(key => {
                            if (reverseMap[key]) {
                                mappedData[reverseMap[key]] = stateData[key];
                            } else {
                                mappedData[key] = stateData[key];
                            }
                        });
                        stateData = mappedData;
                    }

                    if (stateData.propertyAddress !== undefined) setPropertyAddress(stateData.propertyAddress || '');
                    if (stateData.propertyImageUrl !== undefined) setPropertyImageUrl(stateData.propertyImageUrl || '');
                    if (stateData.listingAge !== undefined) setListingAge(stateData.listingAge || '');
                    if (stateData.youtubeViews !== undefined) setYoutubeViews(stateData.youtubeViews || '');
                    if (stateData.youtubeWatchHours !== undefined) setYoutubeWatchHours(stateData.youtubeWatchHours || '');
                    if (stateData.youtubeWatchedEntire !== undefined) setYoutubeWatchedEntire(stateData.youtubeWatchedEntire || '');
                    if (stateData.youtubeAvgWatched !== undefined) setYoutubeAvgWatched(stateData.youtubeAvgWatched || '');
                    if (stateData.zillowViews !== undefined) setZillowViews(stateData.zillowViews || '');
                    if (stateData.zillowSaves !== undefined) setZillowSaves(stateData.zillowSaves || '');
                    if (stateData.mlsMatchedBuyers !== undefined) setMlsMatchedBuyers(stateData.mlsMatchedBuyers || '');
                    if (stateData.mlsSaves !== undefined) setMlsSaves(stateData.mlsSaves || '');
                    if (stateData.realscoutMatchedBuyers !== undefined) setRealscoutMatchedBuyers(stateData.realscoutMatchedBuyers || '');
                    if (stateData.realscoutSaves !== undefined) setRealscoutSaves(stateData.realscoutSaves || '');
                    if (stateData.pastShowings !== undefined) setPastShowings(stateData.pastShowings || '');
                    if (stateData.futureShowings !== undefined) setFutureShowings(stateData.futureShowings || '');
                    if (stateData.openHouses !== undefined) setOpenHouses(stateData.openHouses || '');
                    if (stateData.feedbackSummary !== undefined) setFeedbackSummary(stateData.feedbackSummary || '');
                    if (stateData.otherMarketingData !== undefined) setOtherMarketingData(stateData.otherMarketingData || []);

                    // Marketing data
                    if (stateData.paidCampaigns !== undefined) setPaidCampaigns(stateData.paidCampaigns || []);
                    if (stateData.emailDatabaseSize !== undefined) setEmailDatabaseSize(stateData.emailDatabaseSize || '');
                    if (stateData.emailOpenRate !== undefined) setEmailOpenRate(stateData.emailOpenRate || '');
                    if (stateData.emailCtr !== undefined) setEmailCtr(stateData.emailCtr || '');
                    if (stateData.socialShares !== undefined) setSocialShares(stateData.socialShares || '');
                    if (stateData.socialComments !== undefined) setSocialComments(stateData.socialComments || '');

                    // Competitive context
                    if (stateData.marketAvgDom !== undefined) setMarketAvgDom(stateData.marketAvgDom || '');
                    if (stateData.activeCompetitors !== undefined) setActiveCompetitors(stateData.activeCompetitors || '');
                    if (stateData.recentSales !== undefined) setRecentSales(stateData.recentSales || '');
                    if (stateData.agentRecommendation !== undefined) setAgentRecommendation(stateData.agentRecommendation || '');
                    if (stateData.upcomingActivity !== undefined) setUpcomingActivity(stateData.upcomingActivity || '');
                    if (stateData.youtubeViewsTrend !== undefined) setYoutubeViewsTrend(stateData.youtubeViewsTrend || 'none');
                    if (stateData.zillowViewsTrend !== undefined) setZillowViewsTrend(stateData.zillowViewsTrend || 'none');
                    if (stateData.zillowSavesTrend !== undefined) setZillowSavesTrend(stateData.zillowSavesTrend || 'none');
                    if (stateData.mlsSavesTrend !== undefined) setMlsSavesTrend(stateData.mlsSavesTrend || 'none');

                    // Branding data
                    if (stateData.primaryColor !== undefined) setPrimaryColor(stateData.primaryColor);
                    if (stateData.accentColor !== undefined) setAccentColor(stateData.accentColor);
                    if (stateData.brokerageLogoUrl !== undefined) setBrokerageLogoUrl(stateData.brokerageLogoUrl);
                    if (stateData.reportTitle !== undefined) setReportTitle(stateData.reportTitle);

                    // Local market data
                    if (stateData.interestRateContext !== undefined) setInterestRateContext(stateData.interestRateContext);
                    if (stateData.localInventory !== undefined) setLocalInventory(stateData.localInventory);
                    else if (stateData.townInventory !== undefined) setLocalInventory(stateData.townInventory);

                    if (stateData.buyerDemandLevel !== undefined) setBuyerDemandLevel(stateData.buyerDemandLevel);
                    if (stateData.marketCondition !== undefined) setMarketCondition(stateData.marketCondition);
                    if (stateData.marketHeadwinds !== undefined) setMarketHeadwinds(stateData.marketHeadwinds);
                    if (stateData.showMarketPulse !== undefined) setShowMarketPulse(stateData.showMarketPulse);

                    // Links
                    if (stateData.youtubeLink !== undefined) setYoutubeLink(stateData.youtubeLink);
                    if (stateData.zillowLink !== undefined) setZillowLink(stateData.zillowLink);
                    if (stateData.propertyWebsiteLink !== undefined) setPropertyWebsiteLink(stateData.propertyWebsiteLink);
                    if (stateData.marketingAssetsLink !== undefined) setMarketingAssetsLink(stateData.marketingAssetsLink);

                    // Agent Info from URL (overrides local storage if present)
                    if (stateData.agentName !== undefined) setAgentName(stateData.agentName);
                    if (stateData.agentPhotoUrl !== undefined) setAgentPhotoUrl(stateData.agentPhotoUrl);
                    if (stateData.licenseNumber !== undefined) setLicenseNumber(stateData.licenseNumber);
                    if (stateData.agentPhone !== undefined) setAgentPhone(stateData.agentPhone);
                    if (stateData.agentEmail !== undefined) setAgentEmail(stateData.agentEmail);
                    if (stateData.brokerageName !== undefined) setBrokerageName(stateData.brokerageName);
                    if (stateData.brokeragePhone !== undefined) setBrokeragePhone(stateData.brokeragePhone);
                    if (stateData.brokerageLicense !== undefined) setBrokerageLicense(stateData.brokerageLicense);
                } catch (error) {
                    console.error("Failed to load data from URL:", error);
                }
            }
        };

        loadData();
    }, []);

    // Save agent info to local storage whenever it changes
    useEffect(() => {
        try {
            const agentInfo = {
                agentName, agentPhotoUrl, licenseNumber, agentPhone, agentEmail, brokerageName,
                brokeragePhone, brokerageLicense,
                brokerageLogoUrl, primaryColor, accentColor, reportTitle
            };
            localStorage.setItem('agentInfo', JSON.stringify(agentInfo));
        } catch (error) {
            console.error("Failed to save agent info to local storage:", error);
        }
    }, [agentName, agentPhotoUrl, licenseNumber, agentPhone, agentEmail, brokerageName, brokeragePhone, brokerageLicense, brokerageLogoUrl, primaryColor, accentColor, reportTitle]);

    // Mapping for URL state compression
    const KEY_MAP = {
        propertyAddress: 'pa',
        propertyImageUrl: 'pi',
        listingAge: 'la',
        propertyWebsiteLink: 'pwl',
        youtubeViews: 'yv',
        youtubeWatchHours: 'yh',
        youtubeWatchedEntire: 'ye',
        youtubeAvgWatched: 'ya',
        youtubeLink: 'yl',
        zillowViews: 'zv',
        zillowSaves: 'zs',
        zillowLink: 'zl',
        mlsMatchedBuyers: 'mb',
        mlsSaves: 'ms',
        realscoutMatchedBuyers: 'rb',
        realscoutSaves: 'rs',
        pastShowings: 'ps',
        futureShowings: 'fs',
        openHouses: 'oh',
        feedbackSummary: 'fbs',
        otherMarketingData: 'omd',
        marketingAssetsLink: 'mal',
        paidCampaigns: 'pcp',
        emailDatabaseSize: 'es',
        emailOpenRate: 'eo',
        emailCtr: 'ec',
        socialShares: 'ss',
        socialComments: 'sc',
        marketAvgDom: 'md',
        activeCompetitors: 'ac',
        recentSales: 'rsa',
        agentRecommendation: 'ar',
        upcomingActivity: 'ua',
        youtubeViewsTrend: 'yvt',
        zillowViewsTrend: 'zvt',
        zillowSavesTrend: 'zst',
        mlsSavesTrend: 'mst',
        primaryColor: 'pco',
        accentColor: 'aco',
        brokerageLogoUrl: 'blu',
        reportTitle: 'rt',
        interestRateContext: 'irc',
        localInventory: 'li',
        buyerDemandLevel: 'bdl',
        marketCondition: 'mco',
        marketHeadwinds: 'mhw',
        showMarketPulse: 'smp',
        agentName: 'an',
        agentPhotoUrl: 'apu',
        licenseNumber: 'ln',
        agentPhone: 'aph',
        agentEmail: 'ae',
        brokerageName: 'bn',
        brokeragePhone: 'bp',
        brokerageLicense: 'bl'
    };

    // Helper functions for GZIP compression using native browser APIs
    const compressData = async (data) => {
        const stream = new Blob([JSON.stringify(data)], { type: 'application/json' }).stream();
        const compressedStream = stream.pipeThrough(new CompressionStream('gzip'));
        const chunks = [];
        const reader = compressedStream.getReader();
        while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            chunks.push(value);
        }
        const compressedBlob = new Blob(chunks);
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                const base64 = reader.result.split(',')[1];
                // Convert to Base64URL
                const base64url = base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
                resolve(base64url);
            };
            reader.readAsDataURL(compressedBlob);
        });
    };

    const decompressData = async (input) => {
        try {
            // Restore Base64URL to standard Base64
            let base64 = input.replace(/-/g, '+').replace(/_/g, '/');
            while (base64.length % 4) {
                base64 += '=';
            }

            const binaryString = atob(base64);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
            }
            const stream = new Blob([bytes]).stream();
            const decompressedStream = stream.pipeThrough(new DecompressionStream('gzip'));
            const response = new Response(decompressedStream);
            const text = await response.text();
            return JSON.parse(text);
        } catch (e) {
            console.error("GZIP decompression failed:", e);
            throw e;
        }
    };

    // Function to add a new "Other Marketing Data" field
    const handleAddOtherMarketingField = () => {
        setOtherMarketingData([...otherMarketingData, { id: Date.now(), name: '', value: '' }]);
    };

    // Function to remove an "Other Marketing Data" field
    const handleRemoveOtherMarketingField = (id) => {
        setOtherMarketingData(otherMarketingData.filter(field => field.id !== id));
    };

    // Function to update an "Other Marketing Data" field
    const handleOtherMarketingChange = (id, fieldName, value) => {
        setOtherMarketingData(otherMarketingData.map(field =>
            field.id === id ? { ...field, [fieldName]: value } : field
        ));
    };

    // Functions to manage Paid Campaigns
    const handleAddCampaign = () => {
        setPaidCampaigns([...paidCampaigns, { id: Date.now(), platform: '', spend: '', impressions: '', clicks: '', leads: '' }]);
    };

    const handleRemoveCampaign = (id) => {
        setPaidCampaigns(paidCampaigns.filter(c => c.id !== id));
    };

    const handleCampaignChange = (id, fieldName, value) => {
        setPaidCampaigns(paidCampaigns.map(c =>
            c.id === id ? { ...c, [fieldName]: value } : c
        ));
    };

    const handlePropertyImageUrlChange = (e) => {
        const url = e.target.value;
        if (url.includes('mlsmatrix.com')) {
            alert('Images from the MLS are not publicly accessible and will not work in the report. Please use a different image URL.');
        } else {
            setPropertyImageUrl(url);
        }
    };

    const pluralize = (count, singular, plural) => {
        if (!plural) {
            plural = singular + 's';
        }
        return `${count} ${parseInt(count) === 1 ? singular : plural}`;
    };

    const renderTrendDropdown = (value, onChange) => (
        <select
            className="ml-2 p-3 text-sm border border-gray-300 rounded-lg bg-white"
            style={{ minWidth: '110px' }}
            value={value}
            onChange={(e) => onChange(e.target.value)}
        >
            <option value="none">No Trend</option>
            <option value="up">↗️ Up</option>
            <option value="down">↘️ Down</option>
            <option value="flat">→ Flat</option>
        </select>
    );

    // Helper to calculate CPL for a specific campaign
    const calculateCpl = (campaign) => {
        const spend = parseFloat(String(campaign.spend || '').replace(/[^0-9.]/g, '')) || 0;
        const leads = parseFloat(String(campaign.leads || '').replace(/[^0-9.]/g, '')) || 0;
        if (spend > 0 && leads > 0) {
            return (spend / leads).toFixed(2);
        }
        return '0.00';
    };

    // Helper to format numbers with commas
    const formatWithCommas = (value) => {
        if (!value && value !== 0) return '';
        const num = String(value).replace(/[^0-9.]/g, '');
        if (isNaN(num) || num === '') return value;
        const parts = num.split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        return parts.join('.');
    };

    // Function to generate the HTML for the email report
    const generateReportHtml = () => {
        // Basic email reset styles and general styles for the report
        const styles = `
            /* Basic reset for email clients */
            body, table, td, a { -webkit-text-size-adjust: 100%; -ms-text-size-adjust: 100%; }
            table, td { mso-table-lspace: 0pt; mso-table-rspace: 0pt; }
            img { -ms-interpolation-mode: bicubic; border: 0; height: auto; line-height: 100%; outline: none; text-decoration: none; }
            a[x-apple-data-detectors] { color: inherit !important; text-decoration: none !important; font-size: inherit !important; font-family: inherit !important; font-weight: inherit !important; line-height: inherit !important; }

            /* General styles */
            body { margin: 0; padding: 0; background-color: #f4f4f4; font-family: 'Inter', Arial, sans-serif; }
            .container {
                max-width: 600px;
                margin: 20px auto;
                background-color: #ffffff;
                border-radius: 12px;
                overflow: hidden;
                box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
            }
            .header {
                background-color: ${primaryColor};
                color: #ffffff;
                padding: 20px;
                text-align: center;
                border-top-left-radius: 12px;
                border-top-right-radius: 12px;
            }
            .header h1 {
                margin: 0;
                font-size: 24px;
                font-weight: bold;
            }
            .content {
                padding: 20px 30px;
                color: #333333;
                line-height: 1.6;
            }
            .section-title {
                font-size: 20px;
                font-weight: bold;
                color: ${primaryColor};
                margin-top: 25px;
                margin-bottom: 15px;
                border-bottom: 2px solid ${primaryColor}40;
                padding-bottom: 5px;
                display: flex;
                align-items: center;
                justify-content: space-between;
            }
            .metric-item {
                margin-bottom: 10px;
            }
            .metric-item strong {
                color: ${primaryColor};
            }
            .property-image {
                width: 100%;
                height: auto;
                display: block;
                border-radius: 8px;
                margin-bottom: 20px;
            }
            .footer {
                background-color: ${accentColor};
                color: #64748b;
                padding: 20px;
                text-align: center;
                font-size: 12px;
                border-bottom-left-radius: 12px;
                border-bottom-right-radius: 12px;
                border-top: 1px solid #e2e8f0;
            }
            .address {
                font-size: 18px;
                font-weight: bold;
                color: #1e293b;
                margin-top: 10px;
                margin-bottom: 20px;
                text-align: center;
            }
            .listing-age {
                font-size: 16px;
                color: #475569;
                text-align: center;
                margin-bottom: 20px;
            }
            .interest-rate-context {
                font-size: 14px;
                color: #64748b;
                margin-top: 5px;
            }
            ul {
                list-style-type: disc;
                padding-left: 20px;
                margin-top: 10px;
            }
            li {
                margin-bottom: 8px;
            }
            .agent-info-section {
                text-align: center;
                margin-top: 20px;
                padding-top: 15px;
                border-top: 1px solid #e2e8f0;
            }
            .agent-photo {
                width: 80px;
                height: 80px;
                border-radius: 50%;
                object-fit: cover;
                margin: 0 auto 10px auto;
                display: block;
                border: 2px solid #cbd5e1;
            }
            .brokerage-logo {
                max-width: 150px;
                max-height: 60px;
                margin: 10px auto;
                display: block;
            }
            .agent-name {
                font-size: 18px;
                font-weight: bold;
                color: #1e293b;
                margin-bottom: 5px;
            }
            .agent-details {
                font-size: 14px;
                color: #475569;
            }
            .trend-icon {
                font-size: 14px;
                margin-left: 4px;
            }
            .badge {
                display: inline-block;
                padding: 2px 8px;
                border-radius: 4px;
                font-size: 11px;
                font-weight: bold;
                text-transform: uppercase;
                margin-left: 5px;
            }
            .badge-low { background-color: #fee2e2; color: #991b1b; }
            .badge-moderate { background-color: #fef9c3; color: #854d0e; }
            .badge-high { background-color: #dcfce7; color: #166534; }
            .badge-neutral { background-color: #f1f5f9; color: #475569; }
            
            .market-pulse {
                background-color: #f8fafc;
                border: 1px solid #e2e8f0;
                padding: 15px;
                border-radius: 8px;
                margin-bottom: 25px;
            }
            .market-pulse-grid {
                width: 100%;
                border-collapse: collapse;
            }
            .market-pulse-cell {
                width: 50%;
                padding: 5px;
                vertical-align: top;
            }
            .market-headwinds {
                background-color: #f8fafc;
                border: 1px solid #e2e8f0;
                border-left: 4px solid ${primaryColor}40;
                border-radius: 8px;
                padding: 15px;
                margin-top: 20px;
                color: #475569;
            }
            .headwinds-title {
                font-weight: bold;
                color: ${primaryColor};
                margin-bottom: 5px;
                display: flex;
                align-items: center;
            }
            .recommendation-box {
                background-color: ${primaryColor}10;
                border: 1px solid ${primaryColor}40;
                border-radius: 8px;
                padding: 15px;
                margin-top: 20px;
                color: #334155;
            }
            .recommendation-title {
                font-weight: bold;
                margin-bottom: 5px;
                color: ${primaryColor};
            }
            .activity-list {
                background-color: ${accentColor};
                border: 1px solid #e2e8f0;
                border-radius: 8px;
                padding: 15px;
                margin-top: 20px;
            }
            .activity-title {
                font-weight: bold;
                color: ${primaryColor};
                margin-bottom: 5px;
            }
            .button-link {
                display: inline-block;
                padding: 10px 20px;
                background-color: ${primaryColor};
                color: #ffffff !important;
                text-decoration: none;
                border-radius: 6px;
                font-weight: bold;
                font-size: 14px;
                margin-top: 10px;
                text-align: center;
            }
        `;

        const getTrendIcon = (trend) => {
            if (trend === 'up') return '↗️';
            if (trend === 'down') return '↘️';
            if (trend === 'flat') return '→';
            return ''; // 'none' or default
        };

        const getBadgeClass = (level) => {
            if (level === 'high' || level === 'sellers') return 'badge-high';
            if (level === 'low' || level === 'buyers') return 'badge-low';
            if (level === 'moderate') return 'badge-moderate';
            return 'badge-neutral';
        };

        // Local Market Context (Market Pulse)
        const marketPulseHtml = (showMarketPulse && (interestRateContext || localInventory || (buyerDemandLevel && buyerDemandLevel !== 'none') || (marketCondition && marketCondition !== 'none'))) ? `
            <div class="market-pulse">
                <div style="font-weight: bold; color: ${primaryColor}; margin-bottom: 15px; font-size: 14px; display: flex; align-items: center;">
                    <svg style="width: 16px; height: 16px; margin-right: 6px;" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"></path></svg>
                    Local Market Performance
                </div>
                <table class="market-pulse-grid">
                    <tr>
                        <td class="market-pulse-cell">
                             ${interestRateContext ? `
                                <div>
                                    <span class="badge badge-neutral" style="margin-left:0; background-color: ${primaryColor}15; color: ${primaryColor};">Interest Rates</span>
                                    <div class="interest-rate-context">${interestRateContext}</div>
                                </div>
                            ` : ''}
                        </td>
                        <td class="market-pulse-cell">
                            ${localInventory ? `
                                <div>
                                    <span class="badge badge-neutral" style="margin-left:0; background-color: ${primaryColor}15; color: ${primaryColor};">Inventory</span>
                                    <div class="interest-rate-context">${localInventory}</div>
                                </div>
                            ` : ''}
                        </td>
                    </tr>
                    <tr>
                        <td class="market-pulse-cell">
                            ${(buyerDemandLevel && buyerDemandLevel !== 'none') ? `
                                <div>
                                    <span class="badge badge-${getBadgeClass(buyerDemandLevel)}" style="margin-left:0;">Demand: ${buyerDemandLevel}</span>
                                </div>
                            ` : ''}
                        </td>
                        <td class="market-pulse-cell">
                            ${(marketCondition && marketCondition !== 'none') ? `
                                <div>
                                    <span class="badge badge-${getBadgeClass(marketCondition)}" style="margin-left:0;">Climate: ${marketCondition}</span>
                                </div>
                            ` : ''}
                        </td>
                    </tr>
                </table>
            </div>
        ` : '';

        const renderProgressBar = (label, value) => {
            if (!value) return '';
            // Extract numeric percentage
            const numericValue = parseInt(value.replace(/[^0-9]/g, '')) || 0;
            const cappedValue = Math.min(100, Math.max(0, numericValue));
            return `
                <div style="margin-bottom: 10px;">
                    <div style="font-size: 12px; color: #64748b; margin-bottom: 4px;">${label}: ${value}</div>
                    <div style="width: 100%; bg-color: #e2e8f0; height: 8px; border-radius: 4px; background: #e2e8f0; overflow: hidden;">
                        <div style="width: ${cappedValue}%; height: 100%; background: ${primaryColor}; border-radius: 4px;"></div>
                    </div>
                </div>
            `;
        };

        // Online Engagement Section (conditionally rendered)
        const youtubeMetricsHtml = (youtubeViews || youtubeWatchHours || youtubeWatchedEntire || youtubeAvgWatched) ? `
            <div class="metric-item">
                <strong>YouTube:</strong><br>
                <div style="margin-top: 8px;">
                    ${youtubeViews ? `<div style="margin-bottom: 5px;">${pluralize(formatWithCommas(youtubeViews), 'view')} ${getTrendIcon(youtubeViewsTrend)}</div>` : ''}
                    ${youtubeWatchHours ? `<div style="margin-bottom: 5px;">${pluralize(youtubeWatchHours, 'watch hour')}</div>` : ''}
                    ${renderProgressBar('Watched Entire Video', youtubeWatchedEntire)}
                    ${renderProgressBar('Average Watched', youtubeAvgWatched)}
                    ${youtubeLink ? `
                        <div style="margin-top: 10px;">
                            <a href="${youtubeLink}" class="button-link">Watch Video on YouTube</a>
                        </div>
                    ` : ''}
                </div>
            </div>
        ` : '';

        const zillowMetricsHtml = (zillowViews || zillowSaves) ? `
            <div class="metric-item" style="margin-top: 15px;">
                <strong>Zillow:</strong><br>
                <ul>
                    ${zillowViews ? `<li>${pluralize(formatWithCommas(zillowViews), 'view')} ${getTrendIcon(zillowViewsTrend)}</li>` : ''}
                    ${zillowSaves ? `<li>${pluralize(zillowSaves, 'save')} ${getTrendIcon(zillowSavesTrend)}</li>` : ''}
                </ul>
                ${zillowLink ? `
                    <div style="margin-top: 10px;">
                        <a href="${zillowLink}" class="button-link">View on Zillow</a>
                    </div>
                ` : ''}
            </div>
        ` : '';

        const mlsMetricsHtml = (mlsMatchedBuyers || mlsSaves) ? `
            <div class="metric-item" style="margin-top: 15px;">
                <strong>MLS:</strong><br>
                <ul>
                    ${mlsMatchedBuyers ? `<li>${pluralize(formatWithCommas(mlsMatchedBuyers), 'matched buyer')}</li>` : ''}
                    ${mlsSaves ? `<li>${pluralize(formatWithCommas(mlsSaves), 'save')} ${getTrendIcon(mlsSavesTrend)}</li>` : ''}
                </ul>
            </div>
        ` : '';

        const realscoutMetricsHtml = (realscoutMatchedBuyers || realscoutSaves) ? `
            <div class="metric-item" style="margin-top: 15px;">
                <strong>RealScout (Secondary MLS):</strong><br>
                <ul>
                    ${realscoutMatchedBuyers ? `<li>${pluralize(formatWithCommas(realscoutMatchedBuyers), 'matched buyer')}</li>` : ''}
                    ${realscoutSaves ? `<li>${pluralize(formatWithCommas(realscoutSaves), 'save')}</li>` : ''}
                </ul>
            </div>
        ` : '';

        // Other Marketing Data peer handling
        const filteredOtherMarketingData = otherMarketingData.filter(data => data.name && data.value);

        const otherMarketingHtml = filteredOtherMarketingData.length > 0 ? filteredOtherMarketingData.map(data => `
            <div class="metric-item" style="margin-top: 15px;">
                <strong>${data.name}:</strong><br>
                <ul>
                    <li>${data.value}</li>
                </ul>
            </div>
        `).join('') : '';

        // Marketing Assets link button handled separately at the bottom
        const marketingAssetsHtml = marketingAssetsLink ? `
            <div style="margin-top: 20px; text-align: center; border-top: 1px solid #e2e8f0; padding-top: 15px;">
                <a href="${marketingAssetsLink}" class="button-link" style="background-color: ${accentColor}; color: ${primaryColor} !important; border: 1px solid ${primaryColor}40;">View Marketing Copy & Assets</a>
            </div>
        ` : '';

        // Paid Campaign Blocks
        const paidCampaignsHtml = paidCampaigns.map(campaign => {
            if (!campaign.platform && !campaign.spend && !campaign.impressions && !campaign.clicks && !campaign.leads) return '';
            const cpl = calculateCpl(campaign);
            return `
                <div class="metric-item" style="margin-top: 15px;">
                    <strong>Paid Campaign (${campaign.platform || 'Platform Not Specified'}):</strong><br>
                    <ul>
                        ${campaign.spend ? `<li>Total Ad Spend: $${formatWithCommas(campaign.spend)}</li>` : ''}
                        ${campaign.impressions ? `<li>Impressions: ${formatWithCommas(campaign.impressions)}</li>` : ''}
                        ${campaign.clicks ? `<li>Link Clicks: ${formatWithCommas(campaign.clicks)}</li>` : ''}
                        ${campaign.leads ? `<li>Leads Generated: ${formatWithCommas(campaign.leads)}</li>` : ''}
                        ${(campaign.spend && campaign.leads) ? `<li><strong>Cost Per Lead: $${cpl}</strong></li>` : ''}
                    </ul>
                </div>
            `;
        }).join('');

        const emailMarketingHtml = (emailDatabaseSize || emailOpenRate || emailCtr) ? `
            <div class="metric-item" style="margin-top: 15px;">
                <strong>Email Marketing:</strong><br>
                <ul>
                    ${emailDatabaseSize ? `<li>Sent to Database of: ${formatWithCommas(emailDatabaseSize)}</li>` : ''}
                    ${emailOpenRate ? `<li>Open Rate: ${emailOpenRate}</li>` : ''}
                    ${emailCtr ? `<li>Click-Through Rate: ${emailCtr}</li>` : ''}
                </ul>
            </div>
        ` : '';

        const socialSpecificsHtml = (socialShares || socialComments) ? `
            <div class="metric-item" style="margin-top: 15px;">
                <strong>Social Media Engagement:</strong><br>
                <ul>
                    ${socialShares ? `<li>${pluralize(formatWithCommas(socialShares), 'Share')}</li>` : ''}
                    ${socialComments ? `<li>${pluralize(formatWithCommas(socialComments), 'Comment')}</li>` : ''}
                </ul>
            </div>
        ` : '';

        const onlineEngagementSectionExists = youtubeMetricsHtml || zillowMetricsHtml || mlsMetricsHtml || realscoutMetricsHtml || filteredOtherMarketingData.length > 0 || paidCampaignsHtml || emailMarketingHtml || socialSpecificsHtml || marketingAssetsLink;

        // Showings & Feedback section (conditionally rendered)
        const convertedFeedbackSummary = marked.parse(feedbackSummary || '');
        const showingsFeedbackSectionHtml = (pastShowings || futureShowings || openHouses || convertedFeedbackSummary) ? `
            <div class="section-title">Showings & Feedback</div>
            ${pastShowings ? `<p>We've had <strong>${pluralize(pastShowings, 'past showing')}</strong> for the property.</p>` : ''}
            ${futureShowings ? `<p>We have <strong>${pluralize(futureShowings, 'future showing')}</strong> scheduled.</p>` : ''}
            ${openHouses ? `<p>We have hosted <strong>${pluralize(openHouses, 'open house')}</strong>.</p>` : ''}
            ${convertedFeedbackSummary ? `
                <p><strong>Feedback Summary:</strong></p>
                <div>${convertedFeedbackSummary}</div>
            ` : ''}
        ` : '';

        // Market Analysis rendering
        const strategicAnalysisHtml = (marketAvgDom || activeCompetitors || recentSales || agentRecommendation || upcomingActivity || marketHeadwinds) ? `
            <div class="section-title">Strategic Analysis</div>
            ${(marketAvgDom || activeCompetitors || recentSales) ? `
                <div class="metric-item">
                    <strong>Local Market Context:</strong><br>
                    <ul>
                        ${marketAvgDom ? `<li>Neighborhood Avg. Days on Market: ${marketAvgDom}</li>` : ''}
                        ${activeCompetitors ? `<li>Active Competitors: ${activeCompetitors}</li>` : ''}
                        ${recentSales ? `<li>Recent Sales (30 days): ${recentSales}</li>` : ''}
                    </ul>
                </div>
            ` : ''}
            ${marketHeadwinds ? `
                <div class="market-headwinds">
                    <div class="headwinds-title">
                        <span style="font-weight: bold;">Local Market Headwinds (External Factors)</span>
                    </div>
                    <div>${marked.parse(marketHeadwinds)}</div>
                </div>
            ` : ''}
            ${agentRecommendation ? `
                <div class="recommendation-box">
                    <div class="recommendation-title">Strategic Recommendation:</div>
                    <div>${marked.parse(agentRecommendation)}</div>
                </div>
            ` : ''}
            ${upcomingActivity ? `
                <div class="activity-list">
                    <div class="activity-title">Upcoming Activity:</div>
                    <div>${marked.parse(upcomingActivity)}</div>
                </div>
            ` : ''}
        ` : '';

        // Agent Info Section
        const agentInfoSectionHtml = (agentName || brokerageName) ? `
            <div class="agent-info-section">
                <div class="section-title" style="margin-top:0; border-bottom: none; padding-bottom: 0;">Presented By</div>
                ${agentPhotoUrl ? `<img src="${agentPhotoUrl}" alt="${agentName}" class="agent-photo" onerror="this.onerror=null;this.src='https://placehold.co/80x80/cbd5e1/475569?text=Agent';">` : ''}
                ${agentName ? `<p class="agent-name">${agentName}</p>` : ''}
                <p class="agent-details">
                    ${brokerageName ? `<strong>${brokerageName}</strong><br>` : ''}
                    ${brokerageLicense ? `Brokerage License: ${brokerageLicense}<br>` : ''}
                    ${brokeragePhone ? `Brokerage Phone: ${brokeragePhone}<br>` : ''}
                    ${licenseNumber ? `Agent License: ${licenseNumber}<br>` : ''}
                    ${agentPhone ? `Agent Phone: ${agentPhone}<br>` : ''}
                    ${agentEmail ? `Email: <a href="mailto:${agentEmail}" style="color:#64748b; text-decoration:none;">${agentEmail}</a>` : ''}
                </p>
            </div>
        ` : '';

        // Footer text
        const footerText = (agentName && brokerageName) ? `${agentName} | ${brokerageName}` :
            (agentName ? agentName :
                (brokerageName ? brokerageName : 'Your Real Estate Team'));


        // Full HTML structure for the email
        return `
        <!DOCTYPE html>
        <html lang="en">
        <head>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <title>Property Listing Report</title>
            <style>${styles}</style>
        </head>
        <body>
            <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="border-collapse: collapse;">
                <tr>
                    <td align="center" style="padding: 0;">
                        <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" class="container">
                            <tr>
                                <td class="header">
                                    <h1>${reportTitle || 'Property Listing Report'}</h1>
                                    ${brokerageLogoUrl ? `<img src="${brokerageLogoUrl}" class="brokerage-logo" alt="Brokerage Logo">` : ''}
                                </td>
                            </tr>

                            ${propertyImageUrl ? `
                            <tr>
                                <td style="padding: 20px 30px 0px 30px;">
                                    <img src="${propertyImageUrl}" alt="${propertyAddress}" class="property-image">
                                    ${marketPulseHtml}
                                    <p class="address">${propertyAddress}</p>
                                    ${propertyWebsiteLink ? `
                                        <div style="text-align: center; margin-bottom: 20px;">
                                            <a href="${propertyWebsiteLink}" class="button-link">View Property Website</a>
                                        </div>
                                    ` : ''}
                                    ${listingAge ? `<p class="listing-age">Currently: ${pluralize(listingAge, 'day')} on the market</p>` : ''}
                                </td>
                            </tr>
                            ` : `
                            <tr>
                                <td style="padding: 20px 30px 0px 30px;">
                                    ${marketPulseHtml}
                                    <p class="address">${propertyAddress}</p>
                                    ${propertyWebsiteLink ? `
                                        <div style="text-align: center; margin-bottom: 20px;">
                                            <a href="${propertyWebsiteLink}" class="button-link">View Property Website</a>
                                        </div>
                                    ` : ''}
                                    ${listingAge ? `<p class="listing-age">Currently: ${pluralize(listingAge, 'day')} on the market</p>` : ''}
                                </td>
                            </tr>
                            `}

                            <tr>
                                <td class="content">
                                    ${onlineEngagementSectionExists ? `<div class="section-title"><span>Marketing Activity</span> <span style="font-size: 12px; font-weight: normal; color: #64748b; font-style: italic;">(What we control)</span></div>` : ''}

                                    ${youtubeMetricsHtml}
                                    ${zillowMetricsHtml}
                                    ${mlsMetricsHtml}
                                    ${realscoutMetricsHtml}
                                    ${paidCampaignsHtml}
                                    ${emailMarketingHtml}
                                    ${socialSpecificsHtml}
                                    ${otherMarketingHtml}
                                    ${marketingAssetsHtml}

                                    ${showingsFeedbackSectionHtml}
                                    ${strategicAnalysisHtml}

                                </td>
                            </tr>
                            ${agentInfoSectionHtml ? `<tr><td class="content">${agentInfoSectionHtml}</td></tr>` : ''}
                            <tr>
                                <td class="footer">
                                    <p>&copy; 2025 ${footerText}. All rights reserved.</p>
                                    <p>This report is for informational purposes only.</p>
                                </td>
                            </tr>
                        </table>
                    </td>
                </tr>
            </table>
        </body>
        </html>
        `;
    };

    // Effect to regenerate HTML whenever input states change
    useEffect(() => {
        setReportHtml(generateReportHtml());
    }, [
        propertyAddress, propertyImageUrl, listingAge,
        youtubeViews, youtubeWatchHours, youtubeWatchedEntire, youtubeAvgWatched,
        zillowViews, zillowSaves,
        mlsMatchedBuyers, mlsSaves,
        realscoutMatchedBuyers, realscoutSaves,
        pastShowings, futureShowings, openHouses, feedbackSummary,
        otherMarketingData,
        paidCampaigns,
        emailDatabaseSize, emailOpenRate, emailCtr,
        socialShares, socialComments,
        marketAvgDom, activeCompetitors, recentSales, agentRecommendation, upcomingActivity,
        youtubeViewsTrend, zillowViewsTrend, zillowSavesTrend, mlsSavesTrend,
        primaryColor, accentColor, brokerageLogoUrl, reportTitle,
        interestRateContext, localInventory, buyerDemandLevel, marketCondition, marketHeadwinds,
        agentName, agentPhotoUrl, licenseNumber, agentPhone, agentEmail, brokerageName,
        brokeragePhone, brokerageLicense,
        showMarketPulse,
        youtubeLink, zillowLink, propertyWebsiteLink, marketingAssetsLink
    ]);

    // Function to copy content to clipboard
    const copyToClipboard = (content, message, isHtml = false) => {
        if (isHtml) {
            // For copying raw HTML (for CRM or specific email client HTML editors)
            const textarea = document.createElement('textarea');
            textarea.value = content;
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand('copy');
            document.body.removeChild(textarea);
        } else {
            // For copying rich text from the iframe (for direct paste into Gmail)
            if (iframeRef.current && iframeRef.current.contentWindow) {
                const iframeDoc = iframeRef.current.contentWindow.document;
                const range = iframeDoc.createRange();
                range.selectNodeContents(iframeDoc.body);
                const selection = iframeRef.current.contentWindow.getSelection();
                selection.removeAllRanges();
                selection.addRange(range);
                iframeDoc.execCommand('copy');
                selection.removeAllRanges(); // Deselect after copying
            } else {
                console.error("Iframe not ready or accessible for rich text copy.");
                message = "Could not copy preview. Please try copying the HTML code instead.";
            }
        }

        const messageBox = document.createElement('div');
        messageBox.textContent = message;
        messageBox.style.cssText = `
                    position: fixed;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    background-color: #4CAF50;
                    color: white;
                    padding: 15px 20px;
                    border-radius: 8px;
                    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
                    z-index: 1000;
                    opacity: 0;
                    transition: opacity 0.3s ease-in-out;
                    `;
        document.body.appendChild(messageBox);
        setTimeout(() => {
            messageBox.style.opacity = 1;
        }, 10);
        setTimeout(() => {
            messageBox.style.opacity = 0;
            messageBox.addEventListener('transitionend', () => messageBox.remove());
        }, 2000);
    };

    // Function to reset all fields except agent info
    const handleReset = () => {
        setPropertyAddress('');
        setPropertyImageUrl('');
        setListingAge('');
        setYoutubeViews('');
        setYoutubeWatchHours('');
        setYoutubeWatchedEntire('');
        setYoutubeAvgWatched('');
        setZillowViews('');
        setZillowSaves('');
        setMlsMatchedBuyers('');
        setMlsSaves('');
        setRealscoutMatchedBuyers('');
        setRealscoutSaves('');
        setPastShowings('');
        setFutureShowings('');
        setOpenHouses('');
        setFeedbackSummary('');
        setOtherMarketingData([]);
        setPaidCampaigns([]);
        setEmailDatabaseSize('');
        setEmailOpenRate('');
        setEmailCtr('');
        setSocialShares('');
        setSocialComments('');
        setMarketAvgDom('');
        setActiveCompetitors('');
        setRecentSales('');
        setAgentRecommendation('');
        setUpcomingActivity('');
        setYoutubeViewsTrend('none');
        setZillowViewsTrend('none');
        setZillowSavesTrend('none');
        setMlsSavesTrend('none');
        // Market Context cleaning
        setInterestRateContext('');
        setLocalInventory('');
        setBuyerDemandLevel('none');
        setMarketCondition('none');
        setMarketHeadwinds('');
        setShowMarketPulse(true);
        setYoutubeLink('');
        setZillowLink('');
        setPropertyWebsiteLink('');
        setMarketingAssetsLink('');
        // Agent & brand info fields are intentionally NOT reset
    };

    const handleCopyLink = async () => {
        const dataToEncode = {
            propertyAddress,
            propertyImageUrl,
            listingAge,
            youtubeViews,
            youtubeWatchHours,
            youtubeWatchedEntire,
            youtubeAvgWatched,
            zillowViews,
            zillowSaves,
            mlsMatchedBuyers,
            mlsSaves,
            realscoutMatchedBuyers,
            realscoutSaves,
            pastShowings,
            futureShowings,
            openHouses,
            feedbackSummary,
            otherMarketingData,
            paidCampaigns,
            emailDatabaseSize,
            emailOpenRate,
            emailCtr,
            socialShares,
            socialComments,
            marketAvgDom,
            activeCompetitors,
            recentSales,
            agentRecommendation,
            upcomingActivity,
            youtubeViewsTrend,
            zillowViewsTrend,
            zillowSavesTrend,
            mlsSavesTrend,
            primaryColor,
            accentColor,
            brokerageLogoUrl,
            reportTitle,
            interestRateContext,
            localInventory,
            buyerDemandLevel,
            marketCondition,
            marketHeadwinds,
            showMarketPulse,
            youtubeLink,
            zillowLink,
            propertyWebsiteLink,
            marketingAssetsLink,
            agentName,
            agentPhotoUrl,
            licenseNumber,
            agentPhone,
            agentEmail,
            brokerageName,
            brokeragePhone,
            brokerageLicense
        };

        // Compress data by replacing keys with shortened versions
        const compressedData = { v: 2 };
        Object.keys(dataToEncode).forEach(key => {
            const shortKey = KEY_MAP[key] || key;
            compressedData[shortKey] = dataToEncode[key];
        });

        const encodedData = await compressData(compressedData);
        const url = `${window.location.origin}${window.location.pathname}?data=${encodedData}`;

        navigator.clipboard.writeText(url).then(() => {
            const messageBox = document.createElement('div');
            messageBox.textContent = 'Link copied to clipboard!';
            messageBox.style.cssText = `
                    position: fixed;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    background-color: #4CAF50;
                    color: white;
                    padding: 15px 20px;
                    border-radius: 8px;
                    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
                    z-index: 1000;
                    opacity: 0;
                    transition: opacity 0.3s ease-in-out;
                    `;
            document.body.appendChild(messageBox);
            setTimeout(() => {
                messageBox.style.opacity = 1;
            }, 10);
            setTimeout(() => {
                messageBox.style.opacity = 0;
                messageBox.addEventListener('transitionend', () => messageBox.remove());
            }, 2000);
        });
    };

    return (
        <div className="min-h-screen bg-gray-100 p-4 font-sans antialiased">
            {/* Tailwind CSS CDN */}
            <script src="https://cdn.tailwindcss.com"></script>
            <style>
                {`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&display=swap');
                body {
                    font-family: 'Inter', sans-serif;
                }
                /* Custom scrollbar for the textarea */
                textarea::-webkit-scrollbar {
                    width: 8px;
                }
                textarea::-webkit-scrollbar-track {
                    background: #f1f1f1;
                    border-radius: 10px;
                }
                textarea::-webkit-scrollbar-thumb {
                    background: #888;
                    border-radius: 10px;
                }
                textarea::-webkit-scrollbar-thumb:hover {
                    background: #555;
                }
                `}
            </style>
            <div className="max-w-7xl mx-auto bg-white p-8 rounded-xl shadow-lg flex flex-col lg:flex-row gap-8">
                {/* Input Form Section */}
                <div className="flex-1 lg:w-1/2">
                    <h2 className="text-3xl font-bold text-gray-800 mb-6 border-b-2 pb-2 border-blue-600">Generate Listing Report</h2>

                    {/* Reset Button */}
                    <button
                        onClick={handleReset}
                        className="mb-6 w-full bg-red-500 text-white p-3 rounded-lg hover:bg-red-600 transition duration-200 flex items-center justify-center space-x-2"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                        <span>Reset All Fields (Except Agent & Brand Info)</span>
                    </button>

                    {/* Shareable Edit Link Button */}
                    <button
                        onClick={handleCopyLink}
                        className="mb-6 w-full bg-green-500 text-white p-3 rounded-lg hover:bg-green-600 transition duration-200 flex items-center justify-center space-x-2"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" clipRule="evenodd" />
                        </svg>
                        <span>Copy Edit Link</span>
                    </button>

                    {/* Property Details */}
                    <div className="mb-6 p-4 bg-blue-50 rounded-lg shadow-sm">
                        <h3 className="text-xl font-semibold text-blue-800 mb-4">Property Details</h3>
                        <div className="mb-4">
                            <label htmlFor="propertyAddress" className="block text-gray-700 text-sm font-medium mb-2">Property Address</label>
                            <input
                                type="text"
                                id="propertyAddress"
                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                                value={propertyAddress}
                                onChange={(e) => setPropertyAddress(e.target.value)}
                                placeholder="123 Main St, Anytown, ST"
                            />
                        </div>
                        <div className="mb-4">
                            <label className="block text-gray-700 text-sm font-medium mb-2">Property Website Link</label>
                            <input
                                type="text"
                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                                value={propertyWebsiteLink}
                                onChange={(e) => setPropertyWebsiteLink(e.target.value)}
                                placeholder="e.g., https://123mainst.com"
                            />
                        </div>
                        <div className="mb-4">
                            <label htmlFor="propertyImageUrl" className="block text-gray-700 text-sm font-medium mb-2">Property Image URL (Not From MLS)</label>
                            <input
                                type="text"
                                id="propertyImageUrl"
                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                                value={propertyImageUrl}
                                onChange={handlePropertyImageUrlChange}
                                placeholder="https://example.com/image.jpg"
                            />
                        </div>
                        <div className="mb-4">
                            <label htmlFor="listingAge" className="block text-gray-700 text-sm font-medium mb-2">Listing Age (Days on Market)</label>
                            <input
                                type="number"
                                id="listingAge"
                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                                value={listingAge}
                                onChange={(e) => setListingAge(e.target.value)}
                                placeholder="e.g., 14"
                            />
                        </div>
                    </div>

                    {/* Market Perspective & Conditions */}
                    <div className="mb-6 p-4 bg-slate-100 rounded-lg shadow-sm border-l-4 border-slate-500">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-xl font-semibold text-orange-800">Market Narrative & Positioning</h3>
                            <div className="flex items-center space-x-4">
                                <div className="flex items-center">
                                    <span className="mr-2 text-sm text-gray-600">Market Pulse</span>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input type="checkbox" className="sr-only peer" checked={showMarketPulse} onChange={(e) => setShowMarketPulse(e.target.checked)} />
                                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div>
                                <label className="block text-gray-700 text-sm font-medium mb-2">Interest Rate Environment</label>
                                <input
                                    type="text"
                                    className="w-full p-3 border border-gray-300 rounded-lg"
                                    placeholder="e.g., Rates stabilized at 6.8%"
                                    value={interestRateContext}
                                    onChange={(e) => setInterestRateContext(e.target.value)}
                                />
                            </div>
                            <div>
                                <label className="block text-gray-700 text-sm font-medium mb-2">Local Inventory Level</label>
                                <input
                                    type="text"
                                    className="w-full p-3 border border-gray-300 rounded-lg"
                                    placeholder="e.g., +15% active homes in town"
                                    value={localInventory}
                                    onChange={(e) => setLocalInventory(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-0">
                            <div>
                                <label className="block text-gray-700 text-sm font-medium mb-2">Buyer Demand Level</label>
                                <select
                                    className="w-full p-3 border border-gray-300 rounded-lg bg-white"
                                    value={buyerDemandLevel}
                                    onChange={(e) => setBuyerDemandLevel(e.target.value)}
                                >
                                    <option value="none">⚪ Not Displayed</option>
                                    <option value="low">📉 Low - Limited Activity</option>
                                    <option value="moderate">⚖️ Moderate - Standard Velocity</option>
                                    <option value="high">🚀 High - Intense Interest</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-gray-700 text-sm font-medium mb-2">Market Climate</label>
                                <select
                                    className="w-full p-3 border border-gray-300 rounded-lg bg-white"
                                    value={marketCondition}
                                    onChange={(e) => setMarketCondition(e.target.value)}
                                >
                                    <option value="none">⚪ Not Displayed</option>
                                    <option value="sellers">🏠 Seller's Market (Low Supply)</option>
                                    <option value="neutral">↔️ Neutral Market</option>
                                    <option value="buyers">🏷️ Buyer's Market (High Supply)</option>
                                </select>
                            </div>
                        </div>
                    </div>
                    {/* Online Engagement */}
                    <div className="mb-6 p-4 bg-green-50 rounded-lg shadow-sm">
                        <h3 className="text-xl font-semibold text-green-800 mb-4">Online Engagement</h3>
                        {/* YouTube */}
                        <div className="mb-4">
                            <label className="block text-gray-700 text-sm font-medium mb-2">YouTube Metrics</label>
                            <div className="flex items-center mb-2">
                                <input type="text" className="flex-1 p-3 border border-gray-300 rounded-lg" placeholder="Views (e.g., 1,403)" value={youtubeViews} onChange={(e) => setYoutubeViews(e.target.value)} />
                                {renderTrendDropdown(youtubeViewsTrend, setYoutubeViewsTrend)}
                            </div>
                            <input type="text" className="w-full p-3 border border-gray-300 rounded-lg mb-2" placeholder="Watch Hours (e.g., 121.7)" value={youtubeWatchHours} onChange={(e) => setYoutubeWatchHours(e.target.value)} />
                            <input type="text" className="w-full p-3 border border-gray-300 rounded-lg mb-2" placeholder="Watched Entire Video (e.g., 25%)" value={youtubeWatchedEntire} onChange={(e) => setYoutubeWatchedEntire(e.target.value)} />
                            <input type="text" className="w-full p-3 border border-gray-300 rounded-lg" placeholder="Average Watched (e.g., 43.9%)" value={youtubeAvgWatched} onChange={(e) => setYoutubeAvgWatched(e.target.value)} />
                            <div className="mt-2">
                                <label className="block text-gray-700 text-xs font-medium mb-1">YouTube Video Link</label>
                                <input type="text" className="w-full p-3 border border-gray-300 rounded-lg" placeholder="e.g., https://youtube.com/watch?v=..." value={youtubeLink} onChange={(e) => setYoutubeLink(e.target.value)} />
                            </div>
                        </div>
                        {/* Zillow */}
                        <div className="mb-4">
                            <label className="block text-gray-700 text-sm font-medium mb-2">Zillow Metrics</label>
                            <div className="flex items-center mb-2">
                                <input type="text" className="flex-1 p-3 border border-gray-300 rounded-lg" placeholder="Views (e.g., 583)" value={zillowViews} onChange={(e) => setZillowViews(e.target.value)} />
                                {renderTrendDropdown(zillowViewsTrend, setZillowViewsTrend)}
                            </div>
                            <div className="flex items-center mb-2">
                                <input type="text" className="flex-1 p-3 border border-gray-300 rounded-lg" placeholder="Saves (e.g., 16)" value={zillowSaves} onChange={(e) => setZillowSaves(e.target.value)} />
                                {renderTrendDropdown(zillowSavesTrend, setZillowSavesTrend)}
                            </div>
                            <div className="mt-2">
                                <label className="block text-gray-700 text-xs font-medium mb-1">Zillow Listing Link</label>
                                <input type="text" className="w-full p-3 border border-gray-300 rounded-lg" placeholder="e.g., https://zillow.com/homedetails/..." value={zillowLink} onChange={(e) => setZillowLink(e.target.value)} />
                            </div>
                        </div>
                        {/* MLS */}
                        <div className="mb-4">
                            <label className="block text-gray-700 text-sm font-medium mb-2">MLS Metrics</label>
                            <input type="text" className="w-full p-3 border border-gray-300 rounded-lg mb-2" placeholder="Matched Buyers (e.g., 185)" value={mlsMatchedBuyers} onChange={(e) => setMlsMatchedBuyers(e.target.value)} />
                            <div className="flex items-center mb-2">
                                <input type="text" className="flex-1 p-3 border border-gray-300 rounded-lg" placeholder="Saves (e.g., 2)" value={mlsSaves} onChange={(e) => setMlsSaves(e.target.value)} />
                                {renderTrendDropdown(mlsSavesTrend, setMlsSavesTrend)}
                            </div>
                        </div>
                        {/* RealScout */}
                        <div className="mb-4">
                            <label className="block text-gray-700 text-sm font-medium mb-2">RealScout Metrics</label>
                            <input type="text" className="w-full p-3 border border-gray-300 rounded-lg mb-2" placeholder="Matched Buyers (e.g., 109)" value={realscoutMatchedBuyers} onChange={(e) => setRealscoutMatchedBuyers(e.target.value)} />
                            <input type="text" className="w-full p-3 border border-gray-300 rounded-lg" placeholder="Saves (e.g., 0)" value={realscoutSaves} onChange={(e) => setRealscoutSaves(e.target.value)} />
                        </div>

                        {/* Campaign ROI & Lead Tracking */}
                        <div className="mb-6 help-section-paid-campaigns">
                            <h3 className="text-xl font-semibold text-indigo-800 mb-4">Paid Campaigns & ROI</h3>

                            {paidCampaigns.map(campaign => (
                                <div key={campaign.id} className="mb-6 p-4 bg-white border border-indigo-100 rounded-lg shadow-sm relative">
                                    <button
                                        onClick={() => handleRemoveCampaign(campaign.id)}
                                        className="absolute top-2 right-2 p-1 text-red-400 hover:text-red-600 transition"
                                        title="Remove Campaign"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                        </svg>
                                    </button>

                                    <div className="mb-4">
                                        <label className="block text-gray-700 text-sm font-medium mb-2">Ad Platform</label>
                                        <select
                                            className="w-full p-3 border border-gray-300 rounded-lg"
                                            value={campaign.platform}
                                            onChange={(e) => handleCampaignChange(campaign.id, 'platform', e.target.value)}
                                        >
                                            <option value="">Select Platform</option>
                                            <option value="Facebook/Instagram">Facebook/Instagram</option>
                                            <option value="Google Ads">Google Ads</option>
                                            <option value="LinkedIn">LinkedIn</option>
                                            <option value="Nextdoor">Nextdoor</option>
                                            <option value="YouTube Ads">YouTube Ads</option>
                                            <option value="Other">Other</option>
                                        </select>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-gray-700 text-xs font-medium mb-1">Ad Spend ($)</label>
                                            <input type="text" className="w-full p-2 border border-gray-300 rounded-lg" placeholder="e.g., 250" value={campaign.spend} onChange={(e) => handleCampaignChange(campaign.id, 'spend', e.target.value)} />
                                        </div>
                                        <div>
                                            <label className="block text-gray-700 text-xs font-medium mb-1">Impressions</label>
                                            <input type="text" className="w-full p-2 border border-gray-300 rounded-lg" placeholder="e.g., 10k" value={campaign.impressions} onChange={(e) => handleCampaignChange(campaign.id, 'impressions', e.target.value)} />
                                        </div>
                                        <div>
                                            <label className="block text-gray-700 text-xs font-medium mb-1">Link Clicks</label>
                                            <input type="text" className="w-full p-2 border border-gray-300 rounded-lg" placeholder="e.g., 450" value={campaign.clicks} onChange={(e) => handleCampaignChange(campaign.id, 'clicks', e.target.value)} />
                                        </div>
                                        <div>
                                            <label className="block text-gray-700 text-xs font-medium mb-1">Leads Generated</label>
                                            <input type="text" className="w-full p-2 border border-gray-300 rounded-lg" placeholder="e.g., 12" value={campaign.leads} onChange={(e) => handleCampaignChange(campaign.id, 'leads', e.target.value)} />
                                        </div>
                                    </div>
                                    {(campaign.spend && campaign.leads) && (
                                        <div className="mt-3 text-sm text-indigo-700 font-semibold bg-indigo-50 p-2 rounded text-center">
                                            Cost Per Lead (CPL): ${calculateCpl(campaign)}
                                        </div>
                                    )}
                                </div>
                            ))}

                            <button
                                onClick={handleAddCampaign}
                                className="w-full bg-indigo-600 text-white p-3 rounded-lg hover:bg-indigo-700 transition duration-200 flex items-center justify-center space-x-2"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                                </svg>
                                <span>Add Paid Campaign</span>
                            </button>

                            <h4 className="text-lg font-medium text-gray-700 mb-2 mt-6">Email Marketing & Social</h4>
                            <div className="grid grid-cols-2 gap-2 mb-2">
                                <input type="text" className="p-3 border border-gray-300 rounded-lg" placeholder="Database Size" value={emailDatabaseSize} onChange={(e) => setEmailDatabaseSize(e.target.value)} />
                                <input type="text" className="p-3 border border-gray-300 rounded-lg" placeholder="Open Rate (%)" value={emailOpenRate} onChange={(e) => setEmailOpenRate(e.target.value)} />
                                <input type="text" className="p-3 border border-gray-300 rounded-lg" placeholder="Click-Through Rate (%)" value={emailCtr} onChange={(e) => setEmailCtr(e.target.value)} />
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <input type="text" className="p-3 border border-gray-300 rounded-lg" placeholder="Social Shares" value={socialShares} onChange={(e) => setSocialShares(e.target.value)} />
                                <input type="text" className="p-3 border border-gray-300 rounded-lg" placeholder="Social Comments" value={socialComments} onChange={(e) => setSocialComments(e.target.value)} />
                            </div>
                        </div>

                        {/* Other Marketing Data */}
                        <div className="mb-6 help-section-other-marketing">
                            <h4 className="text-lg font-medium text-gray-700 mb-2">Other Marketing Data</h4>
                            {otherMarketingData.map(field => (
                                <div key={field.id} className="flex items-center space-x-2 mb-2">
                                    <input
                                        type="text"
                                        className="flex-1 p-3 border border-gray-300 rounded-lg"
                                        placeholder="Metric Name (e.g., Google Ads Clicks)"
                                        value={field.name}
                                        onChange={(e) => handleOtherMarketingChange(field.id, 'name', e.target.value)}
                                    />
                                    <input
                                        type="text"
                                        className="flex-1 p-3 border border-gray-300 rounded-lg"
                                        placeholder="Value (e.g., 500 clicks)"
                                        value={field.value}
                                        onChange={(e) => handleOtherMarketingChange(field.id, 'value', e.target.value)}
                                    />
                                    <button
                                        onClick={() => handleRemoveOtherMarketingField(field.id)}
                                        className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition duration-200"
                                        title="Remove field"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm6 0a1 1 0 11-2 0v6a1 1 0 112 0V8z" clipRule="evenodd" />
                                        </svg>
                                    </button>
                                </div>
                            ))}
                            <div className="mt-4">
                                <label className="block text-gray-700 text-sm font-medium mb-2">Marketing Assets / Copy Link</label>
                                <input
                                    type="text"
                                    className="w-full p-3 border border-gray-300 rounded-lg"
                                    placeholder="e.g., https://docs.google.com/..."
                                    value={marketingAssetsLink}
                                    onChange={(e) => setMarketingAssetsLink(e.target.value)}
                                />
                            </div>
                            <button
                                onClick={handleAddOtherMarketingField}
                                className="mt-2 w-full bg-blue-500 text-white p-3 rounded-lg hover:bg-blue-600 transition duration-200 flex items-center justify-center space-x-2"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                                </svg>
                                <span>Add Other Marketing Metric</span>
                            </button>
                        </div>
                    </div>

                    {/* Strategic Market Analysis */}
                    <div className="mb-6 p-4 bg-orange-50 rounded-lg shadow-sm">
                        <h3 className="text-xl font-semibold text-orange-800 mb-4">Market Positioning & Strategic Analysis</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div>
                                <label className="block text-gray-700 text-sm font-medium mb-2">Market Avg. Days on Market</label>
                                <input type="text" className="w-full p-3 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500" placeholder="e.g., 24 days" value={marketAvgDom} onChange={(e) => setMarketAvgDom(e.target.value)} />
                            </div>
                            <div>
                                <label className="block text-gray-700 text-sm font-medium mb-2">Active Competitors</label>
                                <input type="text" className="w-full p-3 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500" placeholder="e.g., 4 similar homes" value={activeCompetitors} onChange={(e) => setActiveCompetitors(e.target.value)} />
                            </div>
                        </div>
                        <div className="mb-4">
                            <label className="block text-gray-700 text-sm font-medium mb-2">Recent Sales (Last 30 Days)</label>
                            <input type="text" className="w-full p-3 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500" placeholder="e.g., 2 homes sold nearby" value={recentSales} onChange={(e) => setRecentSales(e.target.value)} />
                        </div>
                        <div className="mb-4">
                            <label className="block text-slate-700 text-sm font-medium mb-2 flex items-center">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-slate-500" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z" clipRule="evenodd" />
                                </svg>
                                Market Headwinds (External Factors - Markdown Supported)
                            </label>
                            <textarea
                                className="w-full p-3 border border-slate-300 rounded-lg focus:ring-slate-500 focus:border-slate-500 h-24 resize-y"
                                placeholder="Enter external market friction points: '3 nearby price drops', 'Slow season context', etc."
                                value={marketHeadwinds}
                                onChange={(e) => setMarketHeadwinds(e.target.value)}
                            ></textarea>
                        </div>
                        <div className="mb-4">
                            <label className="block text-gray-700 text-sm font-medium mb-2">Agent Strategy Recommendation (Markdown Supported)</label>
                            <textarea
                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500 h-32 resize-y"
                                placeholder="Enter strategy or price adjustment recommendations..."
                                value={agentRecommendation}
                                onChange={(e) => setAgentRecommendation(e.target.value)}
                            ></textarea>
                        </div>
                        <div className="mb-4">
                            <label className="block text-gray-700 text-sm font-medium mb-2">Upcoming Activity (Bullet points - Markdown Supported)</label>
                            <textarea
                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500 h-24 resize-y"
                                placeholder="- Professional video boost&#10;- Open House Sunday 2-4pm&#10;- Targeted Facebook Ad Refresh"
                                value={upcomingActivity}
                                onChange={(e) => setUpcomingActivity(e.target.value)}
                            ></textarea>
                        </div>
                    </div>

                    {/* Showings & Feedback */}
                    <div className="mb-6 p-4 bg-yellow-50 rounded-lg shadow-sm">
                        <h3 className="text-xl font-semibold text-yellow-800 mb-4">Showings & Feedback</h3>
                        <div className="mb-4">
                            <label htmlFor="pastShowings" className="block text-gray-700 text-sm font-medium mb-2">Number of Past Showings</label>
                            <input
                                type="number"
                                id="pastShowings"
                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-yellow-500 focus:border-yellow-500"
                                value={pastShowings}
                                onChange={(e) => setPastShowings(e.target.value)}
                            />
                        </div>
                        <div className="mb-4">
                            <label htmlFor="futureShowings" className="block text-gray-700 text-sm font-medium mb-2">Number of Future Showings</label>
                            <input
                                type="number"
                                id="futureShowings"
                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-yellow-500 focus:border-yellow-500"
                                value={futureShowings}
                                onChange={(e) => setFutureShowings(e.target.value)}
                            />
                        </div>
                        <div className="mb-4">
                            <label htmlFor="openHouses" className="block text-gray-700 text-sm font-medium mb-2">Number of Open Houses</label>
                            <input
                                type="number"
                                id="openHouses"
                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-yellow-500 focus:border-yellow-500"
                                value={openHouses}
                                onChange={(e) => setOpenHouses(e.target.value)}
                            />
                        </div>
                        <div className="mb-4">
                            <label htmlFor="feedbackSummary" className="block text-gray-700 text-sm font-medium mb-2">
                                Feedback Summary (<a href="https://www.markdownguide.org/basic-syntax/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Markdown Supported</a>)
                            </label>
                            <textarea
                                id="feedbackSummary"
                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-yellow-500 focus:border-yellow-500 h-36 resize-y"
                                value={feedbackSummary}
                                onChange={(e) => setFeedbackSummary(e.target.value)}
                                placeholder="Enter feedback here. Use Markdown for formatting: **bold**, *italics*, - list item."
                            ></textarea>
                        </div>
                    </div>

                    {/* Aesthetics & Brokerage Branding */}
                    <div className="mb-6 p-4 bg-gray-50 rounded-lg shadow-sm border border-gray-200">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">Branding & Customization</h3>
                        <div className="mb-4">
                            <label className="block text-gray-700 text-sm font-medium mb-2">Report Title</label>
                            <input
                                type="text"
                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                                placeholder="e.g., Weekly Marketing Update"
                                value={reportTitle}
                                onChange={(e) => setReportTitle(e.target.value)}
                            />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div>
                                <label className="block text-gray-700 text-sm font-medium mb-2">Primary Color (Headers/Titles)</label>
                                <div className="flex items-center space-x-2">
                                    <input
                                        type="color"
                                        className="h-10 w-20 border border-gray-300 rounded-lg"
                                        value={primaryColor}
                                        onChange={(e) => setPrimaryColor(e.target.value)}
                                    />
                                    <input
                                        type="text"
                                        className="flex-1 p-2 border border-gray-300 rounded-lg text-sm"
                                        value={primaryColor}
                                        onChange={(e) => setPrimaryColor(e.target.value)}
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-gray-700 text-sm font-medium mb-2">Accent Color (Backgrounds)</label>
                                <div className="flex items-center space-x-2">
                                    <input
                                        type="color"
                                        className="h-10 w-20 border border-gray-300 rounded-lg"
                                        value={accentColor}
                                        onChange={(e) => setAccentColor(e.target.value)}
                                    />
                                    <input
                                        type="text"
                                        className="flex-1 p-2 border border-gray-300 rounded-lg text-sm"
                                        value={accentColor}
                                        onChange={(e) => setAccentColor(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="mb-4">
                            <label className="block text-gray-700 text-sm font-medium mb-2">Brokerage Logo URL</label>
                            <input
                                type="text"
                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                                placeholder="https://example.com/logo.png"
                                value={brokerageLogoUrl}
                                onChange={(e) => setBrokerageLogoUrl(e.target.value)}
                            />
                            <p className="mt-1 text-xs text-gray-500">Logo will appear in the report header.</p>
                        </div>
                    </div>

                    {/* Agent Information */}
                    <div className="mb-6 p-4 bg-purple-50 rounded-lg shadow-sm">
                        <h3 className="text-xl font-semibold text-purple-800 mb-4">Agent Information (Saved Locally)</h3>
                        <div className="mb-4">
                            <label htmlFor="agentName" className="block text-gray-700 text-sm font-medium mb-2">Agent Name</label>
                            <input type="text" id="agentName" className="w-full p-3 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500" value={agentName} onChange={(e) => setAgentName(e.target.value)} />
                        </div>
                        <div className="mb-4">
                            <label htmlFor="agentPhotoUrl" className="block text-gray-700 text-sm font-medium mb-2">Agent Photo URL</label>
                            <input type="text" id="agentPhotoUrl" className="w-full p-3 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500" value={agentPhotoUrl} onChange={(e) => setAgentPhotoUrl(e.target.value)} />
                        </div>
                        <div className="mb-4">
                            <label htmlFor="licenseNumber" className="block text-gray-700 text-sm font-medium mb-2">License Number</label>
                            <input type="text" id="licenseNumber" className="w-full p-3 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500" value={licenseNumber} onChange={(e) => setLicenseNumber(e.target.value)} />
                        </div>
                        <div className="mb-4">
                            <label htmlFor="agentPhone" className="block text-gray-700 text-sm font-medium mb-2">Phone Number</label>
                            <input type="tel" id="agentPhone" className="w-full p-3 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500" value={agentPhone} onChange={(e) => setAgentPhone(e.target.value)} />
                        </div>
                        <div className="mb-4">
                            <label htmlFor="agentEmail" className="block text-gray-700 text-sm font-medium mb-2">Email</label>
                            <input type="email" id="agentEmail" className="w-full p-3 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500" value={agentEmail} onChange={(e) => setAgentEmail(e.target.value)} />
                        </div>
                        <div className="mb-4">
                            <label htmlFor="brokerageName" className="block text-gray-700 text-sm font-medium mb-2">Brokerage Name</label>
                            <input type="text" id="brokerageName" className="w-full p-3 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500" value={brokerageName} onChange={(e) => setBrokerageName(e.target.value)} />
                        </div>
                        <div className="mb-4">
                            <label htmlFor="brokeragePhone" className="block text-gray-700 text-sm font-medium mb-2">Brokerage Phone</label>
                            <input type="tel" id="brokeragePhone" className="w-full p-3 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500" value={brokeragePhone} onChange={(e) => setBrokeragePhone(e.target.value)} />
                        </div>
                        <div className="mb-4">
                            <label htmlFor="brokerageLicense" className="block text-gray-700 text-sm font-medium mb-2">Brokerage License</label>
                            <input type="text" id="brokerageLicense" className="w-full p-3 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500" value={brokerageLicense} onChange={(e) => setBrokerageLicense(e.target.value)} />
                        </div>
                    </div>
                </div>

                {/* Output Section */}
                <div className="flex-1 lg:w-1/2">
                    <h2 className="text-3xl font-bold text-gray-800 mb-6 border-b-2 pb-2 border-purple-600">Generated Report</h2>
                    <div className="mb-6">
                        <h3 className="text-xl font-semibold text-purple-800 mb-4">Live Preview</h3>
                        <div className="w-full h-[600px] border border-gray-300 rounded-lg overflow-hidden shadow-md bg-white">
                            <iframe
                                ref={iframeRef} // Attach ref to the iframe
                                srcDoc={reportHtml}
                                title="Property Listing Report Preview"
                                className="w-full h-full border-none"
                            ></iframe>
                        </div>
                        <button
                            onClick={() => copyToClipboard(null, 'Preview copied to clipboard!', false)}
                            className="mt-4 w-full bg-purple-600 text-white p-3 rounded-lg hover:bg-purple-700 transition duration-200 flex items-center justify-center space-x-2"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            <span>Copy Preview to Clipboard</span>
                        </button>
                    </div>

                    <div className="mb-6">
                        <h3 className="text-xl font-semibold text-purple-800 mb-4">HTML Code (Copy & Paste)</h3>
                        <textarea
                            className="w-full h-96 p-4 border border-gray-300 rounded-lg bg-gray-50 text-gray-800 font-mono text-sm resize-y"
                            readOnly
                            value={reportHtml}
                            onClick={(e) => e.target.select()} // Select all text on click for easy copying
                        ></textarea>
                        <button
                            onClick={() => copyToClipboard(reportHtml, 'HTML copied to clipboard!', true)}
                            className="mt-4 w-full bg-purple-600 text-white p-3 rounded-lg hover:bg-purple-700 transition duration-200 flex items-center justify-center space-x-2"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
                                <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" />
                            </svg>
                            <span>Copy HTML to Clipboard</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default App;
